import { ReactNode, useState, useRef, useEffect } from 'react';
import { Card, CardMedia, CardContent, Typography, CardActionArea, Box } from '@mui/material';

interface AnimatedCardProps {
  imageUrl: string;
  imageAlt: string;
  title: string;
  description?: string;
  videoUrl?: string | undefined; // Optional video URL for hover preview
  onClick?: () => void;
  children?: ReactNode;
}

export const AnimatedCard = ({
  imageUrl,
  imageAlt,
  title,
  description,
  videoUrl,
  onClick,
  children,
}: AnimatedCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [activeVideo, setActiveVideo] = useState<1 | 2>(1);
  const activeVideoRef = useRef<1 | 2>(1);
  const isSwitchingRef = useRef(false);
  const video1Ref = useRef<HTMLVideoElement>(null);
  const video2Ref = useRef<HTMLVideoElement>(null);
  const animationFrameRef = useRef<number | null>(null);

  // preload both videos on comp mount
  useEffect(() => {
    const video1 = video1Ref.current;
    const video2 = video2Ref.current;
    if (!video1 || !video2 || !videoUrl) return;

    video1.load();
    video2.load();
  }, [videoUrl]);

  // videos that alternate for loop
  useEffect(() => {
    const video1 = video1Ref.current;
    const video2 = video2Ref.current;
    if (!video1 || !video2 || !videoUrl || !isHovered) {
      // cancel animation frame if not hover
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      if (video1) {
        video1.pause();
        video1.currentTime = 0;
      }
      if (video2) {
        video2.pause();
        video2.currentTime = 0;
      }
      return;
    }

    const startVideo = (video: HTMLVideoElement) => {
      video.currentTime = 0;
      const tryPlay = () => {
        if (video.readyState >= 2) {
          video.play().catch(() => {
            // TODO?
          });
        } else {
          setTimeout(tryPlay, 50);
        }
      };
      tryPlay();
    };

    // monitor active video get oyther ready
    const checkLoop = () => {
      if (!isHovered) return;

      const currentActiveNum = activeVideoRef.current;
      const currentActive = currentActiveNum === 1 ? video1 : video2;
      const nextVideo = currentActiveNum === 1 ? video2 : video1;

      if (!currentActive || !nextVideo) return;

      // start the next video early (0.0x seconds before end) to take into
      // account browser delays when swapping
      if (
        !isSwitchingRef.current &&
        currentActive.duration &&
        currentActive.currentTime >= currentActive.duration - 0.078
      ) {
        isSwitchingRef.current = true;

        nextVideo.currentTime = 0;
        nextVideo.play().catch(() => {});

        // switch visibility
        const newActive = currentActiveNum === 1 ? 2 : 1;
        activeVideoRef.current = newActive;
        setActiveVideo(newActive);

        setTimeout(() => {
          isSwitchingRef.current = false;
        }, 100);
      }

      // monitor frame
      animationFrameRef.current = requestAnimationFrame(checkLoop);
    };

    activeVideoRef.current = 1;
    setActiveVideo(1);
    isSwitchingRef.current = false;
    if (isHovered) {
      startVideo(video1);
    }

    // loops to monitor and swap
    if (video1.readyState >= 2) {
      animationFrameRef.current = requestAnimationFrame(checkLoop);
    } else {
      const handleCanPlay = () => {
        animationFrameRef.current = requestAnimationFrame(checkLoop);
        video1.removeEventListener('canplay', handleCanPlay);
      };
      video1.addEventListener('canplay', handleCanPlay);
    }

    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [videoUrl, isHovered]);

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 6,
        },
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <CardActionArea
        onClick={onClick}
        sx={{ height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}
      >
        <Box
          sx={{
            position: 'relative',
            aspectRatio: '16/9',
            overflow: 'hidden',
            backgroundColor: 'black',
            backgroundImage: videoUrl ? `url(${imageUrl})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          {/* thumbnail images when not hovered */}
          <CardMedia
            component="img"
            image={imageUrl}
            alt={imageAlt}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              opacity: isHovered && videoUrl ? 0 : 1,
              transition: 'opacity 0.3s ease-in-out',
            }}
          />
          {/* Video on hover, double for seamless lop */}
          {videoUrl && (
            <>
              <Box
                component="video"
                ref={video1Ref}
                src={videoUrl}
                muted
                playsInline
                preload="auto"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  opacity: isHovered && activeVideo === 1 ? 1 : 0,
                  transition: 'opacity 0.05s linear',
                  pointerEvents: 'none',
                }}
              />
              <Box
                component="video"
                ref={video2Ref}
                src={videoUrl}
                muted
                playsInline
                preload="auto"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  opacity: isHovered && activeVideo === 2 ? 1 : 0,
                  transition: 'opacity 0.05s linear',
                  pointerEvents: 'none',
                }}
              />
            </>
          )}
        </Box>
        <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
          <Typography variant="h5" component="div">
            {title}
          </Typography>
          {!!description && (
            <Typography variant="body1" sx={{ mt: 0.5, display: 'block' }}>
              {description}
            </Typography>
          )}
          {children}
        </CardContent>
      </CardActionArea>
    </Card>
  );
};
